// ─── src/ElevateFeedbackView.tsx ─────────────────────────────────────────────
//
// Elevate Damascus 2026 participant feedback, for staff:
//   * Overview: KPIs, auto-generated highlights and a chart per question.
//   * Stories: quotes participants agreed to publish, and every key takeaway.
//   * Open answers: each free-text question as its own list.
//   * Responses: one row per response, expands to the full answers; staff
//     can delete a response from there (test or spam submissions).
//   * CSV export of every response (Arabic labels, UTF-8 BOM).

import { Fragment, useMemo, useState, type FC, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check, ChevronDown, ChevronRight, Copy, Download, MessageSquareText, Quote, RefreshCw, Search, Trash2,
} from "lucide-react";
import { deleteElevateFeedback, getElevateFeedback, type ElevateFeedback } from "./api/elevate";
import {
  DAY_KEYS, DAY_LABEL, DURATION_LABEL, OPEN_FIELDS, ORG_KEYS, ORG_LABEL, PILLAR_KEYS,
  PILLAR_LABEL, SESSION_LABEL, avg, exportElevateFeedbackCsv, searchableText, summarise,
  type OpenFieldKey,
} from "./components/elevateFeedback/analysis";
import {
  ElevateFeedbackCharts, ElevateFeedbackKpis, HighlightsCard,
} from "./components/elevateFeedback/ElevateFeedbackCharts";
import { StatsSkeleton, TableSkeleton } from "./components/ui/Skeleton";

type Tab = "overview" | "stories" | "open" | "responses";
const TABS: [Tab, string][] = [
  ["overview", "Overview"],
  ["stories", "Stories"],
  ["open", "Open answers"],
  ["responses", "All responses"],
];

const today = () => new Date().toISOString().slice(0, 10);
const when = (iso: string) => new Date(iso).toLocaleString();

export default function ElevateFeedbackView() {
  const [tab, setTab] = useState<Tab>("overview");
  const [search, setSearch] = useState("");

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ["elevate-feedback"],
    queryFn: getElevateFeedback,
    staleTime: 30_000,
  });

  const rows = useMemo(() => data ?? [], [data]);
  const summary = useMemo(() => summarise(rows), [rows]);
  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => (q ? rows.filter((r) => searchableText(r).includes(q)) : rows), [rows, q]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-white text-xl font-bold">Elevate Feedback</h2>
          <p className="text-brand-muted text-sm">
            Participant feedback for Elevate · Damascus 2026: ratings, sessions, pillars and stories.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={tab === key ? "btn-primary text-sm" : "btn-ghost text-sm"}
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="btn-ghost text-sm inline-flex items-center gap-1.5 disabled:opacity-40"
          >
            <RefreshCw size={14} className={isFetching ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={() => exportElevateFeedbackCsv(rows, `elevate_feedback_${today()}.csv`)}
            disabled={!rows.length}
            className="btn-ghost text-sm inline-flex items-center gap-1.5 disabled:opacity-40"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {isLoading && (
        <>
          <StatsSkeleton />
          <TableSkeleton />
        </>
      )}
      {isError && <div className="card p-6 text-red-400 text-sm">Couldn&rsquo;t load Elevate feedback.</div>}

      {data && rows.length === 0 && (
        <div className="card p-10 text-center text-brand-muted">
          <MessageSquareText size={40} strokeWidth={1} className="mx-auto mb-3" />
          <p className="text-white font-medium">No feedback yet</p>
          <p className="text-sm mt-1">Responses to the Elevate participant form will show up here.</p>
        </div>
      )}

      {data && rows.length > 0 && (
        <>
          {tab === "overview" && (
            <>
              <ElevateFeedbackKpis s={summary} />
              <HighlightsCard s={summary} />
              <ElevateFeedbackCharts s={summary} />
            </>
          )}

          {tab !== "overview" && (
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[240px] max-w-md">
                <Search size={14} className="absolute left-3 top-2.5 text-brand-muted" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search all text answers…"
                  dir="auto"
                  className="w-full bg-brand-navy border border-brand-border rounded-lg pl-9 pr-3 py-2 text-sm text-white"
                />
              </div>
              <p className="text-brand-muted text-xs">
                {q ? `${filtered.length} of ${rows.length} responses match` : `${rows.length} responses`}
              </p>
            </div>
          )}

          {tab === "stories" && <Stories rows={filtered} />}
          {tab === "open" && <OpenAnswers rows={rows} q={q} />}
          {tab === "responses" && <ResponsesTable rows={filtered} />}
        </>
      )}
    </div>
  );
}

// ─── Stories ─────────────────────────────────────────────────────────────────

const CopyButton: FC<{ text: string }> = ({ text }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      window.prompt("Copy this quote", text);
    }
  };
  return (
    <button onClick={copy} className="btn-ghost text-xs inline-flex items-center gap-1 shrink-0" dir="ltr">
      {done ? <Check size={12} className="text-brand-green" /> : <Copy size={12} />} {done ? "Copied" : "Copy"}
    </button>
  );
};

function Stories({ rows }: { rows: ElevateFeedback[] }) {
  const quotes = rows.filter((r) => r.quoteConsent && r.quote);
  const takeaways = rows.filter((r) => r.keyTakeaway?.trim());
  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <p className="text-white font-semibold text-sm">Quotes they agreed to publish ({quotes.length})</p>
        {quotes.length === 0 ? (
          <div className="card p-8 text-center text-brand-muted text-sm">No publishable quotes.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {quotes.map((r) => {
              const credit = [r.fullName, r.roleOrg].filter(Boolean).join("، ");
              return (
                <div key={r.id} className="card p-5 flex flex-col" dir="rtl">
                  <div className="flex items-start justify-between gap-2">
                    <Quote size={16} className="text-brand-green" />
                    <CopyButton text={`«${r.quote}»${credit ? ` — ${credit}` : ""}`} />
                  </div>
                  <p className="text-white mt-2 leading-relaxed flex-1">«{r.quote}»</p>
                  <p className="text-white text-sm font-medium mt-3">{r.fullName ?? "—"}</p>
                  {r.roleOrg && <p className="text-brand-muted text-xs">{r.roleOrg}</p>}
                  <p className="text-brand-green text-xs mt-1">التقييم العام {r.ratingOverall}/5 · NPS {r.nps}/10</p>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <p className="text-white font-semibold text-sm">Key takeaways ({takeaways.length})</p>
        {takeaways.length === 0 ? (
          <div className="card p-8 text-center text-brand-muted text-sm">No takeaways match.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {takeaways.map((r) => (
              <div key={r.id} className="card p-4" dir="rtl">
                <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">{r.keyTakeaway}</p>
                <p className="text-brand-muted text-[11px] mt-2" dir="ltr" style={{ textAlign: "right" }}>
                  Overall {r.ratingOverall}/5 · {when(r.createdAt)}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ─── Open answers ────────────────────────────────────────────────────────────

/** Search here matches the answer itself, not anything else in the response. */
function OpenAnswers({ rows, q }: { rows: ElevateFeedback[]; q: string }) {
  const [field, setField] = useState<OpenFieldKey>("weakSession");
  const has = (r: ElevateFeedback, k: OpenFieldKey) => {
    const t = (r[k] ?? "").trim();
    return !!t && (!q || t.toLowerCase().includes(q));
  };
  const counts = Object.fromEntries(OPEN_FIELDS.map(([k]) => [k, rows.filter((r) => has(r, k)).length])) as Record<
    OpenFieldKey,
    number
  >;
  const answers = rows.filter((r) => has(r, field));
  const current = OPEN_FIELDS.find(([k]) => k === field);

  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-3 border-b border-brand-border flex flex-wrap gap-2">
        {OPEN_FIELDS.map(([k, label]) => (
          <button
            key={k}
            onClick={() => setField(k)}
            className={`text-sm rounded-lg px-3 py-1.5 border transition-colors ${
              field === k
                ? "border-brand-green bg-brand-green/10 text-white"
                : "border-brand-border text-brand-muted hover:text-white"
            }`}
          >
            {label} <span className={field === k ? "text-brand-green" : ""}>({counts[k]})</span>
          </button>
        ))}
      </div>
      <div className="px-5 py-3 border-b border-brand-border">
        <p className="text-brand-muted text-xs" dir="rtl">{current?.[2]}</p>
      </div>
      {answers.length === 0 ? (
        <p className="px-5 py-10 text-center text-brand-muted text-sm">No answers{q ? " match" : " to this question"}.</p>
      ) : (
        <ul className="divide-y divide-brand-border">
          {answers.map((r) => (
            <li key={r.id} className="px-5 py-3 flex items-start justify-between gap-4">
              <p className="text-white text-sm whitespace-pre-wrap min-w-0 flex-1" dir="rtl">{r[field]}</p>
              <p className="text-brand-muted text-[11px] shrink-0 mt-0.5">
                {r.ratingOverall}/5 · {new Date(r.createdAt).toLocaleDateString()}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── All responses ───────────────────────────────────────────────────────────

const scoreClass = (v: number | null | undefined, max = 5) => {
  if (v == null) return "text-brand-muted";
  const r = v / max;
  return r >= 0.8 ? "text-emerald-400" : r >= 0.6 ? "text-amber-400" : "text-red-400";
};

const orgAvg = (r: ElevateFeedback) => avg(ORG_KEYS.map((k) => r.organizationRatings?.[k]));

function ResponsesTable({ rows }: { rows: ElevateFeedback[] }) {
  const [open, setOpen] = useState<string | null>(null);
  const cols = ["", "Submitted", "Overall", "NPS", "Speakers", "Networking", "Organisation", "Key takeaway"];
  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-4 border-b border-brand-border">
        <p className="text-white font-semibold text-sm">Responses ({rows.length})</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-border">
              {cols.map((c, i) => (
                <th key={i} className="px-4 py-3 text-left text-brand-muted font-medium whitespace-nowrap">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isOpen = open === r.id;
              return (
                <Fragment key={r.id}>
                  <tr
                    onClick={() => setOpen(isOpen ? null : r.id)}
                    className="border-b border-brand-border/50 hover:bg-brand-border/20 cursor-pointer transition-colors"
                  >
                    <td className="pl-4 py-3 text-brand-muted w-6">
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </td>
                    <td className="px-4 py-3 text-brand-muted whitespace-nowrap">{when(r.createdAt)}</td>
                    <td className={`px-4 py-3 font-semibold ${scoreClass(r.ratingOverall)}`}>{r.ratingOverall}</td>
                    <td className={`px-4 py-3 font-semibold ${scoreClass(r.nps, 10)}`}>{r.nps}</td>
                    <td className={`px-4 py-3 font-semibold ${scoreClass(r.ratingSpeakers)}`}>{r.ratingSpeakers}</td>
                    <td className={`px-4 py-3 font-semibold ${scoreClass(r.ratingNetworking)}`}>{r.ratingNetworking}</td>
                    <td className={`px-4 py-3 font-semibold ${scoreClass(orgAvg(r))}`}>{orgAvg(r) ?? "—"}</td>
                    <td className="px-4 py-3 text-white max-w-[420px] truncate" dir="rtl" title={r.keyTakeaway ?? undefined}>
                      {r.keyTakeaway}
                      {r.quoteConsent && r.quote && <Quote size={12} className="inline text-brand-green mr-2" />}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-brand-border bg-brand-navy/40">
                      <td colSpan={cols.length} className="px-5 py-4">
                        <ResponseDetail r={r} />
                        <DeleteResponse id={r.id} onDeleted={() => setOpen(null)} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={cols.length} className="px-4 py-10 text-center text-brand-muted">No responses match.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Two-click delete: the first click arms it, the second removes the response. */
function DeleteResponse({ id, onDeleted }: { id: string; onDeleted: () => void }) {
  const qc = useQueryClient();
  const [armed, setArmed] = useState(false);
  const del = useMutation({
    mutationFn: () => deleteElevateFeedback(id),
    onSuccess: () => {
      onDeleted();
      qc.invalidateQueries({ queryKey: ["elevate-feedback"] });
    },
  });
  return (
    <div className="mt-4 pt-3 border-t border-brand-border/50 flex flex-wrap items-center justify-end gap-2">
      {del.isError && <span className="text-red-400 text-xs">Couldn&rsquo;t delete this response.</span>}
      {armed && !del.isPending && (
        <button onClick={() => setArmed(false)} className="btn-ghost text-xs">Cancel</button>
      )}
      <button
        onClick={() => (armed ? del.mutate() : setArmed(true))}
        disabled={del.isPending}
        className={`text-xs inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 border transition-colors disabled:opacity-40 ${
          armed ? "bg-red-500/90 border-red-500 text-white hover:bg-red-500" : "border-red-500/40 text-red-400 hover:bg-red-500/10"
        }`}
      >
        <Trash2 size={12} />
        {del.isPending ? "Deleting…" : armed ? "Confirm delete" : "Delete response"}
      </button>
    </div>
  );
}

const Field: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <div className="min-w-0">
    <p className="text-brand-muted text-xs">{label}</p>
    <div className="text-white text-sm mt-0.5 whitespace-pre-wrap">{children}</div>
  </div>
);

function ResponseDetail({ r }: { r: ElevateFeedback }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4" dir="rtl">
      <Field label="أهم ما خرجت به">{r.keyTakeaway || "—"}</Field>
      <Field label="تقييم الأيام">
        <ul className="space-y-0.5">
          {DAY_KEYS.map((d) => (
            <li key={d}>
              <span className="text-brand-muted">{DAY_LABEL[d]}: </span>
              <span className={`font-semibold ${scoreClass(r.dayRatings?.[d])}`}>
                {r.dayRatings?.[d] == null ? "لم يحضر" : `${r.dayRatings[d]}/5`}
              </span>
            </li>
          ))}
        </ul>
      </Field>
      <Field label="الجلسات الأكثر فائدة">
        {(r.topSessions ?? []).length ? (
          <ul className="list-disc pr-4">
            {r.topSessions.map((k) => <li key={k}>{SESSION_LABEL[k] ?? k}</li>)}
          </ul>
        ) : "—"}
      </Field>
      <Field label="محاور البرنامج">
        <ul className="space-y-0.5">
          {PILLAR_KEYS.map((p) => (
            <li key={p}>
              <span className="text-brand-muted">{PILLAR_LABEL[p]}: </span>
              <span className={`font-semibold ${scoreClass(r.pillarRatings?.[p])}`}>{r.pillarRatings?.[p] ?? "—"}/5</span>
            </li>
          ))}
        </ul>
      </Field>
      <Field label="تنظيم البرنامج">
        <ul className="space-y-0.5">
          {ORG_KEYS.map((k) => (
            <li key={k}>
              <span className="text-brand-muted">{ORG_LABEL[k]}: </span>
              <span className={`font-semibold ${scoreClass(r.organizationRatings?.[k])}`}>{r.organizationRatings?.[k] ?? "—"}/5</span>
            </li>
          ))}
        </ul>
      </Field>
      <Field label="التفاعل والحوارات وورشات العمل">{r.ratingInteraction ?? "—"}/5</Field>
      <Field label="مدة البرنامج">{DURATION_LABEL[r.durationFit] ?? r.durationFit}</Field>
      <Field label="الثقة في إرشاد مؤسس شركة ناشئة">{r.mentorConfidence ?? "—"}/5</Field>
      {OPEN_FIELDS.filter(([k]) => r[k]).map(([k, , ar]) => (
        <Field key={k} label={ar}>{r[k]}</Field>
      ))}
      {r.quoteConsent && r.quote && (
        <div className="lg:col-span-2 rounded-lg bg-brand-green/10 border border-brand-green/30 px-3 py-2 text-sm text-white">
          «{r.quote}»
          {(r.fullName || r.roleOrg) && (
            <span className="text-brand-muted"> — {[r.fullName, r.roleOrg].filter(Boolean).join("، ")}</span>
          )}
          <span className="block text-brand-green text-[11px] mt-0.5">وافق على نشر رأيه</span>
        </div>
      )}
    </div>
  );
}
